import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar, Tag } from 'lucide-react';
import AppLink from '../components/AppLink';

const News = () => {
  const { language, t } = useLanguage();
  const [newsArticles, setNewsArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await fetch('/api/news/');
        if (response.ok) setNewsArticles(await response.json());
      } catch (error) {
        console.error('Error fetching news:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-party-red mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-party-green py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t({ en: 'Latest News & Updates', bn: 'সর্বশেষ সংবাদ ও আপডেট' })}
          </h1>
          <p className="text-xl text-green-100 max-w-3xl mx-auto">
            {t({
              en: 'Stay informed about our activities, initiatives, and developments.',
              bn: 'আমাদের কার্যক্রম, উদ্যোগ এবং উন্নয়ন সম্পর্কে অবগত থাকুন।',
            })}
          </p>
        </div>
      </section>

      {/* News Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {newsArticles.map((article) => (
              <AppLink key={article._id || article.id} to={`/news/${article._id || article.id}`}>
                <Card className="overflow-hidden hover:shadow-2xl transition-all duration-300 h-full">
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={article.image_url || article.image}
                      alt={t(article.title)}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <Badge className="bg-party-red-100 text-party-red hover:bg-party-red-100">
                        <Tag className="h-3 w-3 mr-1" />
                        {t(article.category)}
                      </Badge>
                      <div className="flex items-center text-gray-500 text-sm">
                        <Calendar className="h-4 w-4 mr-1" />
                        {new Date(article.date || article.created_at).toLocaleDateString(language === 'en' ? 'en-US' : 'bn-BD')}
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{t(article.title)}</h3>
                    <p className="text-gray-600 leading-relaxed">{t(article.excerpt || article.content)}</p>
                  </CardContent>
                </Card>
              </AppLink>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default News;